import { Grid } from "@mui/material";
import React from "react";
import Title from "src/components/common/Title";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";
import PageLayoutController from "src/components/core/PageLayoutController";
import GuestBanner from "src/components/dashboard/GuestBanner";
import { useTransactionLowHigh } from "src/components/transactions/helperfns";
import MobileFilter from "src/components/transactions/MobileFilter";
import TransactionsList from "src/components/transactions/TransactionsList";
import { RawTransactions } from "src/components/transactions/types";
import api from "src/store/features/api";
import {
  GetUserCurrencyTransactionsApiArg,
  PaginatedCTsRes,
} from "src/store/features/api/generated";
import { addTransactionNote } from "src/store/features/transactions.slice";
import { useAppDispatch } from "src/store/store";
import useParamChange from "src/utils/hooks/useParamChange";
import usePermission from "src/utils/hooks/usePermission";
import Preloader from "src/utils/Preloader";

const DESKTOP_LAYOUT = [[".", ".", ".", ".", ".", "."]];

const MOBILE_LAYOUT = [
  ["HEADING", "FILTER"],
  ["TABLE", "TABLE"],
];

export const DEFAULT_TRANSACTION_PARAMS: GetUserCurrencyTransactionsApiArg = {
  limit: 10,
  page: 1,
  baseCurrency: "",
  device: "mobile",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  txType: "all",
  currency: "",
  startDate: undefined,
  endDate: undefined,
  startTxSize: undefined,
  endTxSize: undefined,
};

const TransactionMobile = () => {
  const dispatch = useAppDispatch();
  const [currentParamState, setParamsCallback] = useParamChange<GetUserCurrencyTransactionsApiArg>(
    DEFAULT_TRANSACTION_PARAMS,
  );

  const {
    data: transactions,
    isFetching,
    isSuccess,
    refetch,
  } = api.useGetUserCurrencyTransactionsQuery(currentParamState, { pollingInterval: 360000 }) as {
    data: PaginatedCTsRes;
    isFetching: boolean;
    isSuccess: boolean;
    refetch: any;
  };

  // Add transaction notes to the redux
  React.useEffect(() => {
    if (transactions) {
      transactions.data.forEach((tx) => {
        if (tx.note) {
          dispatch(addTransactionNote({ id: tx.id, note: tx.note }));
        }
      });
    }
  }, [transactions]);

  const lowHigh = useTransactionLowHigh(transactions);
  const { userPermission } = usePermission();

  return (
    <PageLayoutController
      mobileLayout={MOBILE_LAYOUT}
      desktopLayout={DESKTOP_LAYOUT}
      spacing={15}
      container={true}>
      {/* HEADING */}
      <ComponentLayoutWrapper
        gridArea="HEADING"
        state={{ isLoading: false }}
        fadeInDirection="none"
        loadingComponent={<></>}>
        <Title title={{ partB: "History" }} />
      </ComponentLayoutWrapper>

      {/* FILTER */}
      <ComponentLayoutWrapper
        gridArea="FILTER"
        state={{ isLoading: false }}
        fadeInDirection="none"
        loadingComponent={<></>}>
        <MobileFilter
          transactions={transactions as RawTransactions}
          currentParams={currentParamState}
          setParams={setParamsCallback}
          refetch={refetch}
          lowHigh={lowHigh}
        />
      </ComponentLayoutWrapper>

      {/* TABLE */}
      <ComponentLayoutWrapper
        gridArea="TABLE"
        state={{ isLoading: isFetching }}
        fadeInDirection="none"
        loadingComponent={<Preloader />}>
        {userPermission === "guest" && (
          <GuestBanner
            variant={2}
            descriptionPrefix="To view your transactions"
            featureName="Transactions"
          />
        )}
        {transactions && transactions?.data.length <= 0 ? (
          <Grid container justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            <Grid item>
              Not seeing your transaction here? speak to your broker for further assistance
            </Grid>
          </Grid>
        ) : (
          <TransactionsList
            transactions={transactions as RawTransactions}
            isFetching={isFetching}
            isSuccess={isSuccess}
            currentParamState={currentParamState}
            setParams={setParamsCallback}
          />
        )}
      </ComponentLayoutWrapper>
    </PageLayoutController>
  );
};

export default TransactionMobile;
